import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { Button } from 'components/Button'
import { Header } from 'components/Header'
import { InputWithFloatPlaceholder } from 'components/InputWithFloatPlaceholder'

import { resetErrorAction } from 'root-redux/actions/common'
import {
  LOGIN_WITH_LEARNWORLDS_PASSWORD,
  loginWithLearnworldsPasswordAction,
} from 'root-redux/actions/user'
import { selectActionList, selectError } from 'root-redux/selects/common'
import { TAppDispatch } from 'root-redux/store/store'

import { eventLogger } from 'services/eventLogger.service'

import securityPasswordIconSvg from 'assets/images/sprite/security-password.svg'

import { EVENT_SOURCE } from 'root-constants'

import { StyledPasswordCheckGate as S } from './PasswordCheckGate.styles'

type TProps = {
  email: string
}

export const PasswordCheckGate: React.FC<TProps> = ({ email }) => {
  const dispatch: TAppDispatch = useDispatch()
  const { t } = useTranslation()

  const fetchingActionsList = useSelector(selectActionList)
  const errorMessage = useSelector(selectError)

  // Deliberately not usePasswordInputField: its length rule would reject an existing
  // LearnWorlds password shorter than our own minimum before the backend ever saw it.
  const [password, setPassword] = useState('')

  const isFetching = useMemo(
    () => fetchingActionsList.includes(LOGIN_WITH_LEARNWORLDS_PASSWORD),
    [fetchingActionsList],
  )

  useEffect(() => {
    eventLogger.logLoginPageShow(EVENT_SOURCE.UNSUBSCRIBE_LOGIN)
  }, [])

  useEffect(() => {
    return () => {
      dispatch(resetErrorAction())
    }
  }, [dispatch])

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault()

      if (!password || isFetching) return

      dispatch(loginWithLearnworldsPasswordAction(email, password))
    },
    [dispatch, email, isFetching, password],
  )

  return (
    <S.Wrapper>
      <Header />

      <S.Container>
        <S.Title>{t('subscription.title')}</S.Title>

        <S.Form id="login-form" onSubmit={handleSubmit}>
          <InputWithFloatPlaceholder
            disabled
            value={email}
            labelName={t('login.email')}
            marginBottom={12}
          />

          <InputWithFloatPlaceholder
            type="password"
            value={password}
            isValid={!errorMessage}
            validationText={errorMessage}
            hasVisibilityControl
            // Prompts while empty, names the field once the label floats.
            labelName={
              password ? t('login.password') : t('login.passwordPlaceholder')
            }
            onChange={(e) => {
              setPassword(e.target.value)
              dispatch(resetErrorAction())
            }}
          />
        </S.Form>
      </S.Container>

      <S.FooterContainer>
        <Button
          type="submit"
          form="login-form"
          onClick={handleSubmit}
          disabled={!password || isFetching}
        >
          {t('button.login')}
        </Button>

        <S.Hint>
          <S.HintIcon svg={securityPasswordIconSvg} />
          {t('login.passwordHint')}
        </S.Hint>
      </S.FooterContainer>
    </S.Wrapper>
  )
}
